/**
 * Owner: Masa Bayu
 * File: world-facets.ts
 * Date:Time: 2026-09-27 06:31
 * Description: Marketplace language + country facets — Publication Language ≠ UI.
 */

export type WorldFacetOption = {
  readonly id: string;
  readonly label: string;
};

/** Publication-language primaries for discovery (presentation labels · not UI i18n). */
export const MARKETPLACE_LANGUAGE_OPTIONS: readonly WorldFacetOption[] = Object.freeze([
  { id: 'en', label: 'English' },
  { id: 'ms', label: 'Malay' },
  { id: 'id', label: 'Indonesian' },
  { id: 'ar', label: 'Arabic' },
  { id: 'zh', label: 'Chinese' },
  { id: 'ja', label: 'Japanese' },
  { id: 'ko', label: 'Korean' },
  { id: 'hi', label: 'Hindi' },
  { id: 'ta', label: 'Tamil' },
  { id: 'th', label: 'Thai' },
  { id: 'vi', label: 'Vietnamese' },
  { id: 'tl', label: 'Filipino' },
  { id: 'fr', label: 'French' },
  { id: 'de', label: 'German' },
  { id: 'es', label: 'Spanish' },
  { id: 'pt', label: 'Portuguese' },
  { id: 'it', label: 'Italian' },
  { id: 'nl', label: 'Dutch' },
  { id: 'pl', label: 'Polish' },
  { id: 'tr', label: 'Turkish' },
  { id: 'ru', label: 'Russian' },
  { id: 'uk', label: 'Ukrainian' },
  { id: 'el', label: 'Greek' },
  { id: 'he', label: 'Hebrew' },
  { id: 'fa', label: 'Persian' },
  { id: 'ur', label: 'Urdu' },
  { id: 'bn', label: 'Bengali' },
  { id: 'am', label: 'Amharic' },
]);

/**
 * Country / region focus for discovery (≠ language).
 * Ids match catalog region tags via marketplace regionId facet.
 */
export const MARKETPLACE_COUNTRY_OPTIONS: readonly WorldFacetOption[] = Object.freeze([
  { id: 'malaysia', label: 'Malaysia' },
  { id: 'singapore', label: 'Singapore' },
  { id: 'indonesia', label: 'Indonesia' },
  { id: 'brunei', label: 'Brunei' },
  { id: 'thailand', label: 'Thailand' },
  { id: 'vietnam', label: 'Vietnam' },
  { id: 'philippines', label: 'Philippines' },
  { id: 'japan', label: 'Japan' },
  { id: 'korea', label: 'South Korea' },
  { id: 'china', label: 'China' },
  { id: 'india', label: 'India' },
  { id: 'saudi_arabia', label: 'Saudi Arabia' },
  { id: 'uae', label: 'United Arab Emirates' },
  { id: 'egypt', label: 'Egypt' },
  { id: 'turkey', label: 'Türkiye' },
  { id: 'united_kingdom', label: 'United Kingdom' },
  { id: 'united_states', label: 'United States' },
  { id: 'canada', label: 'Canada' },
  { id: 'australia', label: 'Australia' },
  { id: 'new_zealand', label: 'New Zealand' },
  { id: 'france', label: 'France' },
  { id: 'germany', label: 'Germany' },
  { id: 'spain', label: 'Spain' },
  { id: 'brazil', label: 'Brazil' },
  { id: 'mexico', label: 'Mexico' },
  { id: 'nigeria', label: 'Nigeria' },
  { id: 'south_africa', label: 'South Africa' },
]);

export function filterWorldFacetOptions(
  options: readonly WorldFacetOption[],
  query: string,
): readonly WorldFacetOption[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) {
    return options;
  }
  return options.filter(
    (opt) =>
      opt.label.toLowerCase().includes(needle) || opt.id.toLowerCase().includes(needle),
  );
}

export function worldFacetLabel(
  options: readonly WorldFacetOption[],
  id: string,
): string | null {
  if (id.length === 0) {
    return null;
  }
  const hit = options.find((opt) => opt.id === id);
  return hit?.label ?? id;
}
